import i18n from 'i18next';
import { toNumber } from 'lodash';
import { StateCreator } from 'zustand';

import { DashboardVehicle } from '../dashboardStore/types/DashboardVehicle';
import { UpdateVehicleParams } from './types/UpdateVehicleParams';
import { VehicleConfirmationState } from './types/VehicleConfirmationState';
import { VehicleConfirmationStoreDependencies } from './types/VehicleConfirmationStoreDependencies';
import { VehicleMake } from './types/VehicleMake';
import { VehicleModel } from './types/VehicleModel';
import { VehicleTrim } from './types/VehicleTrim';

export const vehicleConfirmationStore =
  ({
    useApiStore,
  }: VehicleConfirmationStoreDependencies): StateCreator<VehicleConfirmationState> =>
  (set) => ({
    fetchVehicleMakesFailedMessage: null,
    fetchVehicleModelsFailedMessage: null,
    fetchVehicleTrimsFailedMessage: null,
    makes: [],
    models: [],
    trims: [],
    updateVehicleFailedMessage: null,
    updateVehicleSuccess: false,

    fetchVehicleMakes: async (year: string) => {
      set({
        fetchVehicleMakesFailedMessage: null,
        makes: [],
        models: [],
        trims: [],
      });

      if (!year) {
        return;
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get<never, VehicleMake[]>(`/vehicles/years/${year}/makes`)
        .then((makes) => set({ makes }))
        .catch(() =>
          set({
            fetchVehicleMakesFailedMessage: i18n.t(
              'errors.fetchVehicleMakes.defaultMessage',
            ),
          }),
        );
    },
    fetchVehicleModels: async (year: string, make: string) => {
      set({
        fetchVehicleModelsFailedMessage: null,
        models: [],
        trims: [],
      });

      if (!year || !make) {
        return;
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get<never, VehicleModel[]>(
          `/vehicles/years/${year}/makes/${make}/models`,
        )
        .then((models) => set({ models }))
        .catch(() =>
          set({
            fetchVehicleModelsFailedMessage: i18n.t(
              'errors.fetchVehicleModels.defaultMessage',
            ),
          }),
        );
    },
    fetchVehicleTrims: async (year: string, make: string, model: string) => {
      set({ fetchVehicleTrimsFailedMessage: null, trims: [] });

      if (!year || !make || !model) {
        return;
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get<never, VehicleTrim[]>(
          `/vehicles/years/${year}/makes/${make}/models/${model}/trims`,
        )
        .then((trims) => set({ trims }))
        .catch(() =>
          set({
            fetchVehicleTrimsFailedMessage: i18n.t(
              'errors.fetchVehicleTrims.defaultMessage',
            ),
          }),
        );
    },
    updateVehicle: async (params: UpdateVehicleParams) => {
      if (!params || !params.vehicle || !params.vehicleId) {
        return;
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      const {
        vehicle: { make, model, trim, trimId, year },
        vehicleId,
      } = params;

      return portalHttpRequest
        .put<never, DashboardVehicle>(`/2.0/vehicles/${vehicleId}`, {
          make,
          model,
          trim,
          trimId,
          year: toNumber(year),
        })
        .then(() => set({ updateVehicleSuccess: true }))
        .catch(() => {
          set({
            updateVehicleFailedMessage: i18n.t(
              'errors.updateVehicle.defaultMessage',
            ),
          });
        });
    },
  });
